import { useEffect, useState } from "react";
import { ShieldCheck, ExternalLink, Newspaper } from "lucide-react";
import API from "../lib/api";
import { Reveal } from "./Motion";
import { TESTIMONIALS, PRESS, LEARNER_COUNT_MIN, FACTS } from "../data/proof";
import { VERIFY } from "../data/content";

export const ProofBand = () => {
  const [stats, setStats] = useState(null);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    API.get("/stats/public").then((r) => setStats(r.data || null)).catch(() => {});
    API.get("/testimonials/public").then((r) => setStories(r.data?.items || [])).catch(() => {});
  }, []);

  const learners = stats?.learners;
  const freeStarts = stats?.free_starts;
  const showCount = typeof learners === "number" && learners >= LEARNER_COUNT_MIN;
  const showStarts = typeof freeStarts === "number" && freeStarts >= LEARNER_COUNT_MIN;
  const wall = [...TESTIMONIALS, ...stories];

  return (
    <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 lg:px-10" data-testid="home-proof">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="label text-sage mb-3">Where we are</p>
            <h2 className="font-serif text-3xl md:text-5xl text-navy-deep leading-tight">Small, new, and verifiable.</h2>
          </div>
          <a href={VERIFY.dsbs} target="_blank" rel="noreferrer" data-testid="proof-verify-vosb" className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold transition-colors">
            <ShieldCheck size={18} /> Verify our VOSB certification on SBA DSBS <ExternalLink size={14} />
          </a>
        </div>
      </Reveal>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="proof-facts">
        {FACTS.map((f, i) => (
          <Reveal key={f.s} delay={i * 0.06}>
            <div className="rounded-2xl bg-white border border-navy/10 p-6 h-full">
              <p className="font-serif text-3xl md:text-4xl text-navy-deep">{f.n}</p>
              <p className="mt-2 text-sm text-navy-deep/65 leading-snug">{f.s}</p>
            </div>
          </Reveal>
        ))}
        {showStarts && (
          <Reveal delay={0.25}>
            <div className="rounded-2xl bg-navy text-cream p-6 h-full" data-testid="proof-free-starts">
              <p className="font-serif text-3xl md:text-4xl">{freeStarts.toLocaleString()}</p>
              <p className="mt-2 text-sm text-cream/70 leading-snug">people have started the free Introduction</p>
            </div>
          </Reveal>
        )}
        {showCount && (
          <Reveal delay={0.3}>
            <div className="rounded-2xl bg-navy text-cream p-6 h-full" data-testid="proof-learner-count">
              <p className="font-serif text-3xl md:text-4xl">{learners.toLocaleString()}</p>
              <p className="mt-2 text-sm text-cream/70 leading-snug">learners signed up</p>
            </div>
          </Reveal>
        )}
      </div>

      {PRESS.length > 0 && (
        <Reveal delay={0.15}>
          <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm" data-testid="proof-press">
            {PRESS.map((p) => (
              <li key={p.url} className="inline-flex items-center gap-2 text-navy-deep/70">
                <Newspaper size={14} className="text-navy" />
                <span className="label text-navy/50">{p.date}</span>
                <a href={p.url} target="_blank" rel="noreferrer" className="font-semibold text-navy hover:text-gold transition-colors">{p.outlet}</a>
                <span>— {p.title}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      )}

      {wall.length > 0 ? (
        <div className="mt-6 grid md:grid-cols-3 gap-4" data-testid="proof-testimonials">
          {wall.map((t, i) => (
            <Reveal key={t.id || t.quote} delay={i * 0.08}>
              <figure className="h-full rounded-2xl bg-sage/15 border border-sage/30 p-7">
                <blockquote className="font-serif text-xl text-navy-deep leading-snug">&ldquo;{t.quote}&rdquo;</blockquote>
                <figcaption className="mt-5 flex items-center gap-3 text-sm text-navy-deep/65">
                  {t.photo && <img src={t.photo} alt={`${t.name}, ${t.role}`} className="w-10 h-10 rounded-full object-cover" loading="lazy" referrerPolicy="no-referrer" />}
                  <span><span className="font-semibold text-navy-deep">{t.name}</span> · {t.role}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      ) : (
        <Reveal delay={0.2}>
          <p className="mt-6 text-sm text-navy-deep/55 max-w-2xl" data-testid="proof-note">
            We opened in December 2025 and we don't publish numbers or quotes we can't stand behind. Learner words appear here as the first free-lesson learners share them — each one with permission, first name and role, read by a person before it goes up.
          </p>
        </Reveal>
      )}
    </section>
  );
};
